import AsyncStorage from '@react-native-async-storage/async-storage'
import { create } from 'zustand'
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware'
import { useCardStore } from '@/src/common/hooks/useCardInformation'
import type { AuthTokenResponseDto, BaseAuthTokenResponseDto } from '@api/mesa/graphql'
import { DateTime } from 'luxon'
import { Api, store } from '@/src/store'
import { AuthenticationApi } from '@/src/experiences/authentication/store'

const accessTokenSetAtKey = 'accessTokenSetAt'
const accessTokenExpiresAtKey = 'accessTokenExpiresAt'

export const ASYNCSTORAGE_TOKEN_STORE_KEY = 'access-token-storage'

type ResponseTokens =
  | AuthTokenResponseDto // on first auth
  | BaseAuthTokenResponseDto // on refresh

type Tokens = ResponseTokens & {
  [accessTokenSetAtKey]: Date
  [accessTokenExpiresAtKey]: Date
}

interface TokenStore {
  tokens?: Tokens
  setEntireResponse: (response: ResponseTokens) => void
  clearTokens: () => void
  hasTokens: () => boolean
}

export const useTokenStoreLoaded = create<{
  loaded: boolean
  setLoaded(): void
}>((set) => ({
  loaded: false,
  setLoaded() {
    set({ loaded: true })
  },
}))

const { promise: tokenStoreLoadingPromise, resolve: resolveTokenStoreLoadingPromise } =
  Promise.withResolvers<TokenStore | undefined>()

void tokenStoreLoadingPromise.then(() => useTokenStoreLoaded.getState().setLoaded())

export { tokenStoreLoadingPromise }

export const useTokenStore = create(
  subscribeWithSelector(
    persist<TokenStore>(
      (set, getState) => ({
        tokens: undefined,
        setEntireResponse(tokens) {
          const accessTokenSetAt = new Date()

          const latestTokens = {
            ...getState().tokens,
            ...tokens,
            accessTokenSetAt,
            [accessTokenExpiresAtKey]: DateTime.fromJSDate(accessTokenSetAt)
              // subtract 8 minutes to account for clock skew
              .plus({ seconds: tokens.expiresIn, minutes: -8 })
              .toJSDate(),
          }

          set({
            tokens: latestTokens,
          })

          void store.dispatch(
            AuthenticationApi.util.upsertQueryData('getToken', undefined, { token: latestTokens }),
          )
        },
        clearTokens() {
          set({ tokens: undefined })
          void store.dispatch(Api.util.resetApiState())
        },
        hasTokens() {
          return Boolean(getState().tokens)
        },
      }),
      {
        name: ASYNCSTORAGE_TOKEN_STORE_KEY,
        storage: createJSONStorage(() => AsyncStorage, {
          reviver(key, value) {
            if (typeof value !== 'string') return value
            switch (key) {
              case accessTokenSetAtKey:
              case accessTokenExpiresAtKey:
                return new Date(value)
              default:
                return value
            }
          },
        }),
        onRehydrateStorage() {
          return resolveTokenStoreLoadingPromise
        },
      },
    ),
  ),
)

useTokenStore.subscribe(
  (state) => state.tokens?.accessToken,
  function handleUserInformation(accessToken) {
    if (!accessToken) {
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      useCardStore.getState().setCardInformation(undefined)
    }
  },
)
